import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const CameraViewfinder = ({ onCapture, onCancel, isCapturing }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [facingMode, setFacingMode] = useState('user');

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const mediaStream = await navigator.mediaDevices?.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err?.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera permissions in your browser settings.');
      } else if (err?.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Unable to access camera. Please check your device settings.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef?.current || !canvasRef?.current) return;

    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    const context = canvas?.getContext('2d');

    canvas.width = video?.videoWidth;
    canvas.height = video?.videoHeight;

    context?.drawImage(video, 0, 0, canvas?.width, canvas?.height);

    canvas?.toBlob((blob) => {
      const imageUrl = URL.createObjectURL(blob);
      onCapture(imageUrl, blob);
    }, 'image/jpeg', 0.95);
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const handleRetry = () => {
    setError(null);
    startCamera();
  };

  if (error) {
    return (
      <div className="w-full h-full bg-muted rounded-xl flex flex-col items-center justify-center p-4 md:p-6 lg:p-8">
        <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full bg-error/10 flex items-center justify-center mb-4 md:mb-6">
          <Icon name="CameraOff" size={32} className="md:w-10 md:h-10 lg:w-12 lg:h-12 text-error" />
        </div>
        <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground mb-2 md:mb-3 text-center">Camera Access Required</h3>
        <p className="text-sm md:text-base lg:text-lg text-muted-foreground text-center mb-4 md:mb-6 max-w-md">{error}</p>
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full sm:w-auto">
          <Button variant="default" onClick={handleRetry} iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-background rounded-xl overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 bg-muted flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-3 md:gap-4">
            <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground">Initializing camera...</p>
          </div>
        </div>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />

      <canvas ref={canvasRef} className="hidden" />

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 border-2 border-primary/30 rounded-xl m-4 md:m-6 lg:m-8"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-64 md:w-56 md:h-72 lg:w-64 lg:h-80 border-2 border-primary rounded-3xl"></div>
      </div>

      <div className="absolute top-4 right-4 md:top-6 md:right-6 lg:top-8 lg:right-8 pointer-events-auto">
        <button
          onClick={switchCamera}
          className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center shadow-elevation-2 hover:bg-card transition-smooth"
          aria-label="Switch camera"
        >
          <Icon name="SwitchCamera" size={20} className="md:w-6 md:h-6 lg:w-7 lg:h-7" />
        </button>
      </div>

      <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-3 md:gap-4 lg:gap-6 px-4 md:px-6 lg:px-8 pointer-events-auto">
        <Button
          variant="outline"
          size="lg"
          onClick={onCancel}
          className="bg-card/90 backdrop-blur-sm"
        >
          Cancel
        </Button>

        <button
          onClick={capturePhoto}
          disabled={isCapturing || isLoading}
          className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-elevation-3 transition-smooth touch-target"
          aria-label="Capture photo"
        >
          <div className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full border-4 border-white"></div>
        </button>

        <div className="w-16 md:w-20 lg:w-24"></div>
      </div>

      <div className="absolute top-4 left-4 md:top-6 md:left-6 lg:top-8 lg:left-8 bg-card/90 backdrop-blur-sm rounded-lg px-3 py-2 md:px-4 md:py-2.5 lg:px-5 lg:py-3 shadow-elevation-2 pointer-events-none">
        <p className="text-xs md:text-sm lg:text-base text-foreground font-medium">Position your face in the frame</p>
      </div>
    </div>
  );
};

export default CameraViewfinder;


